import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useListCategories, useCreateCategory, useUpdateCategory, useDeleteCategory, getListCategoriesQueryKey, useListProducts } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, Plus, Edit, Trash2, ChevronDown, ChevronRight, Search } from "lucide-react";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/currency";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

export default function Categories() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: categories, isLoading } = useListCategories();
  const { data: products } = useListProducts({});
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [categorySearch, setCategorySearch] = useState("");

  useEffect(() => {
    if (window.location.search.includes("add=1")) setIsCreateOpen(true);
  }, []);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "" },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    if (editingId) {
      updateCategory.mutate({ id: editingId, data }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
          setEditingId(null);
          form.reset();
        },
        onError: () => toast({ title: "Failed to update category", variant: "destructive" }),
      });
    } else {
      createCategory.mutate({ data }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
          setIsCreateOpen(false);
          form.reset();
        },
        onError: () => toast({ title: "Failed to save category", variant: "destructive" }),
      });
    }
  };

  const handleEdit = (category: any) => {
    setEditingId(category.id);
    form.reset({ name: category.name, description: category.description || "" });
  };

  const handleDelete = (id: number, name: string) => {
    const affectedProducts = getProductsForCategory(id);
    const count = affectedProducts.length;
    const confirmMsg = count > 0
      ? `"${name}" has ${count} product${count === 1 ? "" : "s"} assigned to it. Deleting this category will unset their category. Continue?`
      : `Are you sure you want to delete "${name}"?`;
    if (confirm(confirmMsg)) {
      deleteCategory.mutate({ id }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
          toast({ title: `Category "${name}" deleted` });
        },
        onError: () => toast({ title: "Failed to delete category", variant: "destructive" }),
      });
    }
  };

  const toggleCategory = (id: number) => {
    setExpandedCategory(prev => prev === id ? null : id);
  };

  const getProductsForCategory = (categoryId: number) =>
    (products || []).filter(p => p.categoryId === categoryId);
  const filteredCategories = (categories ?? []).filter(category =>
    `${category.name} ${category.description ?? ""}`.toLocaleLowerCase().includes(categorySearch.trim().toLocaleLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Categories</h1>
          <p className="text-muted-foreground mt-1">Manage product categories.</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingId(null); form.reset({ name: "", description: "" }); }}>
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Category</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={createCategory.isPending}>Save</Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={editingId !== null} onOpenChange={(o) => !o && setEditingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={updateCategory.isPending}>Update</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          aria-label="Search categories"
          data-testid="input-search-managed-categories"
          placeholder="Search categories…"
          value={categorySearch}
          onChange={event => setCategorySearch(event.target.value)}
          className="pl-9"
        />
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader className="bg-background">
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
            ) : categories?.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No categories found</TableCell></TableRow>
            ) : filteredCategories.length === 0 && categories?.length ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No categories match “{categorySearch}”.</TableCell></TableRow>
            ) : filteredCategories.map((cat) => {
              const catProducts = getProductsForCategory(cat.id);
              const isExpanded = expandedCategory === cat.id;
              return (
                <>
                  <TableRow
                    key={cat.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleCategory(cat.id)}
                  >
                    <TableCell className="pr-0">
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </TableCell>
                    <TableCell className="font-medium">{cat.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{cat.description || "—"}</TableCell>
                    <TableCell>
                      {catProducts.length > 0
                        ? <Badge variant="secondary">{catProducts.length} product{catProducts.length !== 1 ? "s" : ""}</Badge>
                        : <span className="text-xs text-muted-foreground">No products</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(cat.createdAt), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(cat)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDelete(cat.id, cat.name)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow key={`${cat.id}-products`} className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={6} className="p-0">
                        <div className="px-6 py-3 border-t border-dashed">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            All {cat.name} Products ({catProducts.length})
                          </p>
                          {catProducts.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">No products in this category yet.</p>
                          ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {catProducts.map(p => (
                                <div key={p.id} className="flex items-center justify-between bg-background border rounded-lg px-3 py-2 gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{p.name}</p>
                                    <p className="text-xs text-muted-foreground">{(p as any).brand ?? "No brand"}</p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-sm font-semibold">{formatCurrency(Number(p.price))}</p>
                                    <p className={`text-xs ${Number(p.stock) <= Number(p.reorderLevel ?? 5) ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
                                      {p.stock} in stock
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
